import { useState, useEffect } from 'react';

const NativeNatural = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('native-natural');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const nativeVarieties = [
    {
      name: 'Desi Tomatoes',
      description: 'Traditional Indian tomato varieties with rich flavor',
      image: 'https://readdy.ai/api/search-image?query=Traditional%20Indian%20desi%20tomatoes%20growing%20on%20plants%2C%20heirloom%20tomato%20varieties%2C%20organic%20native%20tomatoes%2C%20rich%20red%20color%2C%20natural%20farming%2C%20heritage%20vegetable%20cultivation%2C%20authentic%20Indian%20produce&width=400&height=300&seq=desi-tomatoes&orientation=landscape',
      benefits: ['Rich in antioxidants', 'Better taste', 'Disease resistant', 'Adapted to local climate']
    },
    {
      name: 'Indigenous Brinjal',
      description: 'Native eggplant varieties in various shapes and colors',
      image: 'https://readdy.ai/api/search-image?query=Indigenous%20Indian%20brinjal%20eggplant%20varieties%2C%20different%20shapes%20and%20colors%20of%20native%20eggplants%2C%20traditional%20vegetable%20cultivation%2C%20organic%20farming%2C%20heritage%20vegetables%2C%20natural%20biodiversity&width=400&height=300&seq=native-brinjal&orientation=landscape',
      benefits: ['High nutrition', 'Natural pest resistance', 'Unique flavors', 'Cultural significance']
    },
    {
      name: 'Traditional Okra',
      description: 'Local bhindi varieties with superior quality',
      image: 'https://readdy.ai/api/search-image?query=Traditional%20Indian%20okra%20bhindi%20plants%20with%20pods%2C%20native%20vegetable%20varieties%2C%20organic%20okra%20cultivation%2C%20green%20healthy%20pods%2C%20sustainable%20farming%2C%20heritage%20vegetables%2C%20natural%20growing%20methods&width=400&height=300&seq=traditional-okra&orientation=landscape',
      benefits: ['High fiber content', 'Medicinal properties', 'Heat tolerance', 'Long shelf life']
    },
    {
      name: 'Native Leafy Greens',
      description: 'Indigenous spinach and amaranth varieties',
      image: 'https://readdy.ai/api/search-image?query=Native%20Indian%20leafy%20greens%20and%20spinach%20varieties%2C%20traditional%20amaranth%20plants%2C%20indigenous%20green%20vegetables%2C%20organic%20cultivation%2C%20nutritious%20leafy%20vegetables%2C%20heritage%20crops%2C%20natural%20farming&width=400&height=300&seq=native-greens&orientation=landscape',
      benefits: ['High iron content', 'Drought resistant', 'Nutritional density', 'Easy to grow']
    },
    {
      name: 'Heritage Chilies',
      description: 'Traditional Indian chili varieties with authentic heat',
      image: 'https://readdy.ai/api/search-image?query=Traditional%20Indian%20chili%20varieties%20growing%20on%20plants%2C%20heritage%20hot%20peppers%2C%20native%20spice%20cultivation%2C%20red%20and%20green%20chilies%2C%20organic%20spice%20farming%2C%20authentic%20Indian%20chilies%2C%20natural%20heat%20and%20flavor&width=400&height=300&seq=heritage-chilies&orientation=landscape',
      benefits: ['Authentic flavor', 'Natural heat levels', 'Medicinal uses', 'Cultural importance']
    },
    {
      name: 'Indigenous Gourds',
      description: 'Native bottle gourd and ridge gourd varieties',
      image: 'https://readdy.ai/api/search-image?query=Indigenous%20Indian%20gourds%20bottle%20gourd%20and%20ridge%20gourd%2C%20traditional%20climbing%20vegetables%2C%20native%20gourd%20varieties%2C%20organic%20cultivation%2C%20healthy%20green%20gourds%2C%20heritage%20vegetable%20farming%2C%20natural%20growing%20methods&width=400&height=300&seq=native-gourds&orientation=landscape',
      benefits: ['Water content', 'Cooling properties', 'Digestive benefits', 'Versatile uses']
    }
  ];

  const seedTypes = [
    {
      category: 'Vegetable Seeds',
      varieties: ['Tomato', 'Brinjal', 'Okra', 'Chili', 'Gourd'],
      icon: 'ri-plant-line',
      color: 'text-green-600'
    },
    {
      category: 'Leafy Greens',
      varieties: ['Spinach', 'Amaranth', 'Fenugreek', 'Coriander', 'Mint'],
      icon: 'ri-leaf-line',
      color: 'text-emerald-600'
    },
    {
      category: 'Grain Seeds',
      varieties: ['Rice', 'Millet', 'Wheat', 'Barley', 'Sorghum'],
      icon: 'ri-seedling-line',
      color: 'text-amber-600'
    },
    {
      category: 'Herb Seeds',
      varieties: ['Basil', 'Turmeric', 'Ginger', 'Curry Leaves', 'Lemongrass'],
      icon: 'ri-flower-line',
      color: 'text-purple-600'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-green-50 to-lime-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Native & Natural Section
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Preserving India's traditional crops for future generations
          </p>
          
          {/* Mission Statement */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg max-w-2xl mx-auto">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-earth-line text-2xl text-green-600"></i>
            </div>
            <p className="text-lg font-medium text-gray-800">
              "Preserving India's traditional crops for future generations"
            </p>
          </div>
        </div>

        {/* Seed Categories */}
        <div className={`grid grid-cols-1 md:grid-cols-4 gap-6 mb-16 transform transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          {seedTypes.map((seedType, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-100 to-lime-100 flex items-center justify-center ${seedType.color}`}>
                <i className={`${seedType.icon} text-2xl`}></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 text-center">{seedType.category}</h3>
              <div className="space-y-1">
                {seedType.varieties.map((variety, idx) => (
                  <div key={idx} className="flex items-center text-sm text-gray-600">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                    <span>{variety}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Native Varieties Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {nativeVarieties.map((variety, index) => (
            <div 
              key={index}
              className={`group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
              style={{ transitionDelay: `${400 + index * 100}ms` }}
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={variety.image}
                  alt={variety.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute top-3 right-3">
                  <span className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Non-GMO
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{variety.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{variety.description}</p>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-700">Benefits:</h4>
                  <div className="grid grid-cols-2 gap-1">
                    {variety.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center text-xs text-gray-600">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mt-4 flex items-center text-green-600">
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-shield-check-line text-sm"></i>
                  </div>
                  <span className="text-xs font-medium">Heritage Variety</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Conservation Message */}
        <div className={`bg-gradient-to-r from-green-600 to-lime-600 rounded-3xl p-8 md:p-12 text-white transform transition-all duration-1000 delay-800 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-plant-line text-3xl"></i>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-6">
                Biodiversity Conservation
              </h3>
              <p className="text-xl text-green-100 leading-relaxed">
                Our commitment to preserving native varieties ensures genetic diversity, 
                supports local ecosystems, and maintains the authentic flavors of traditional Indian cuisine
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-dna-line text-2xl"></i>
                </div>
                <h4 className="text-xl font-bold mb-2">Genetic Diversity</h4>
                <p className="text-green-100">Preserving unique genetic traits for future resilience</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-restaurant-line text-2xl"></i>
                </div>
                <h4 className="text-xl font-bold mb-2">Authentic Flavors</h4>
                <p className="text-green-100">Maintaining traditional tastes and nutritional profiles</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-global-line text-2xl"></i>
                </div>
                <h4 className="text-xl font-bold mb-2">Ecosystem Support</h4>
                <p className="text-green-100">Supporting local wildlife and beneficial insects</p>
              </div>
            </div>

            <div className="text-center">
              <button className="bg-white text-green-600 hover:bg-green-50 px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer mr-4">
                Get Native Seeds
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
                Learn More
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NativeNatural;